import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserIcon, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CollaborationUser {
  id: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  last_active: string;
  isViewer?: boolean;
}

interface ActiveUsersIndicatorProps {
  users: CollaborationUser[];
  className?: string;
  maxVisible?: number;
}

export const ActiveUsersIndicator: React.FC<ActiveUsersIndicatorProps> = ({
  users,
  className,
  maxVisible = 5,
}) => {
  // Group users by ID to handle multiple sessions per user
  // This properly shows when a user has multiple active sessions (e.g., multiple tabs/devices)
  const userMap = new Map<string, CollaborationUser & { sessionCount: number }>();

  users.forEach(user => {
    const existing = userMap.get(user.id);
    if (existing) {
      existing.sessionCount++;
    } else {
      userMap.set(user.id, { ...user, sessionCount: 1 });
    }
  });

  const uniqueUsers = Array.from(userMap.values());

  if (uniqueUsers.length === 0) {
    return null;
  }

  const visibleUsers = uniqueUsers.slice(0, maxVisible);
  const remainingCount = Math.max(0, uniqueUsers.length - maxVisible);

  return (
    <TooltipProvider>
      <div className={cn('flex items-center gap-2', className)}>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          <span>
            {uniqueUsers.length} {uniqueUsers.length === 1 ? 'user' : 'users'} online
          </span>
        </div>

        <div className="flex -space-x-2">
          {visibleUsers.map(user => (
            <Tooltip key={user.id}>
              <TooltipTrigger asChild>
                <Avatar
                  className={cn(
                    'h-8 w-8 border-2 border-background',
                    user.isViewer && 'ring-2 ring-muted-foreground/50',
                  )}
                >
                  <AvatarImage src={user.avatar_url || ''} alt={user.name || 'User'} />
                  <AvatarFallback
                    className={cn('text-xs', user.isViewer ? 'bg-muted' : 'bg-primary')}
                  >
                    {user.name ? (
                      user.name.charAt(0).toUpperCase()
                    ) : (
                      <UserIcon className="h-3 w-3" />
                    )}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-1">
                  <p className="font-medium">{user.name || 'Anonymous'}</p>
                  {user.email && <p className="text-xs text-muted-foreground">{user.email}</p>}
                  {(user as any).sessionCount > 1 && (
                    <p className="text-xs text-muted-foreground">
                      {(user as any).sessionCount} active sessions
                    </p>
                  )}
                  {user.isViewer && (
                    <p className="text-xs text-muted-foreground italic">Viewing via shared link</p>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          ))}

          {remainingCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Avatar className="h-8 w-8 border-2 border-background">
                  <AvatarFallback className="text-xs bg-muted">+{remainingCount}</AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {remainingCount} more {remainingCount === 1 ? 'user' : 'users'}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
